import { MathUtils, Matrix4, Object3D, Quaternion, Vector3 } from 'three';
import { getCalibrationDataForFrame } from '../../cross-team-shared/misc-utils/projection-utils';
import {
  CalibrationOptions,
  QuaternionFormatCorrection,
} from '../../cross-team-shared/state/calibration-options-state';
import { Point3D } from '../../team-apis/otterdam/answers/shape-typings';
import {
  CalibrationData,
  SingleFrameCalibrationData,
  XYZWQuaternionTuple,
} from '../../team-apis/otterdam/calibration-data';
import { addPoints } from './point-utils';

// ThreeJS cameras naturally look the negative Z direction (before
// rotation). Data come in as if cameras naturally look in positive X
// direction. So start by rotating the camera to look along
// positive X before applying the calibration.
export const internalInitialRotation: Point3D = [90, -90, 0];

const globalQuaternion = new Quaternion();
const globalVector3 = new Vector3();
const globalObject3d = new Object3D();

export function getCameraData(
  calibrationData: CalibrationData,
  calibrationOptions: CalibrationOptions,
  frameNumber: number,
): SingleFrameCalibrationData {
  const frameData = getCalibrationDataForFrame(frameNumber, calibrationData);
  const { position, rotation } = calculateCameraPose(
    frameData,
    calibrationOptions,
  );
  return { ...frameData, position, rotation };
}

export function getCameraWorldDirection(
  rotation: XYZWQuaternionTuple,
): Point3D {
  const cameraRotation = globalQuaternion.fromArray(rotation);

  const cameraDefaultDirection = globalVector3.set(0, 0, -1);
  return cameraDefaultDirection.applyQuaternion(cameraRotation).toArray();
}

export function buildCalibrationMatrix(calibrationData: CalibrationData) {
  const frameData = getCalibrationDataForFrame(0, calibrationData);

  const translation = globalVector3.fromArray(frameData.position);
  const rotation = globalQuaternion.fromArray(frameData.rotation);
  const scale = new Vector3(1, 1, 1);

  return new Matrix4().compose(translation, rotation, scale);
}

function calculateCameraPose(
  frameData: SingleFrameCalibrationData,
  options: CalibrationOptions,
) {
  const rotation = frameData.rotation;
  let position = frameData.position;

  const { initialRotation, quaternionFormatCorrections, shouldInvert } =
    options;

  const correctedRotation = correctQuaternion(
    rotation,
    quaternionFormatCorrections,
  );

  globalQuaternion.fromArray(correctedRotation);

  if (shouldInvert) {
    position = position.map((p) => p * -1) as Point3D;
    globalQuaternion.invert();
  }

  return {
    position,
    rotation: applyRotationToInitial(globalQuaternion, initialRotation),
  };
}

function applyRotationToInitial(quaternion: Quaternion, initial: Point3D) {
  const combined = addPoints(internalInitialRotation, initial).map(
    MathUtils.degToRad,
  ) as Point3D;

  globalObject3d.rotation.fromArray(combined);
  globalObject3d.applyQuaternion(quaternion);

  return quaternion
    .setFromEuler(globalObject3d.rotation)
    .toArray() as XYZWQuaternionTuple;
}

function correctQuaternion(
  quaternion: [number, number, number, number],
  correction: QuaternionFormatCorrection,
) {
  switch (correction) {
    case 'none':
      return quaternion;
    case 'xyzwAsWxyz':
      return XYZWQuaternionAsWXYZ(quaternion);
    case 'wxyzAsXyzw':
      return WXYZQuaternionAsXYZW(quaternion);
  }
}

// Client labeled quaternions as XYZW but actually provided WXYZ.
function XYZWQuaternionAsWXYZ(
  quaternion: [number, number, number, number],
): XYZWQuaternionTuple {
  return [quaternion[1], quaternion[2], quaternion[3], quaternion[0]];
}

// Client labeled quaternions as WXYZ but actually provided XYZW.
function WXYZQuaternionAsXYZW(
  quaternion: [number, number, number, number],
): XYZWQuaternionTuple {
  return [quaternion[3], quaternion[0], quaternion[1], quaternion[2]];
}
